/**
 * Base Monitoring System for Wixell Platform
 * Provides Sentry integration and basic error tracking
 */

import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { replayIntegration } from '@sentry/react';

// Sentry configuration
const SENTRY_DSN = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_SENTRY_DSN : process.env.VITE_SENTRY_DSN;
const ENVIRONMENT = (typeof import.meta !== 'undefined' && import.meta.env) ? (import.meta.env.MODE || 'development') : (process.env.NODE_ENV || 'development');

/**
 * Initialize Sentry for error tracking
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('[Monitoring] Sentry DSN not configured, using fallback mode');
    initFallbackMonitoring();
    return;
  }

  try {
    console.log('[Monitoring] Initializing Sentry...');
    console.log('[Monitoring] Sentry available:', typeof Sentry !== 'undefined');
    console.log('[Monitoring] BrowserTracing available:', typeof browserTracingIntegration !== 'undefined');
    console.log('[Monitoring] Replay available:', typeof replayIntegration !== 'undefined');
    
    if (typeof Sentry === 'undefined') {
      console.error('[Monitoring] Sentry is not available, using fallback mode');
      initFallbackMonitoring();
      return;
    }

    const integrations = [];
    
    // Add BrowserTracing if available
    if (typeof browserTracingIntegration === 'function') {
      try {
        integrations.push(browserTracingIntegration({
          tracingOrigins: ['localhost', /^\//, 'https://wixell.com', 'https://staging.wixell.com'],
        }));
        console.log('[Monitoring] BrowserTracing integration added');
      } catch (tracingError) {
        console.warn('[Monitoring] BrowserTracing initialization failed:', tracingError);
      }
    } else {
      console.warn('[Monitoring] BrowserTracing is not available');
    }

    // Add Replay integration if available
    if (typeof replayIntegration === 'function') {
      try {
        integrations.push(replayIntegration({
          sessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
          errorSampleRate: 1.0,
        }));
        console.log('[Monitoring] Replay integration added');
      } catch (replayError) {
        console.warn('[Monitoring] Replay initialization failed:', replayError);
      }
    } else {
      console.warn('[Monitoring] Replay is not available');
    }

    // Initialize Sentry with comprehensive configuration
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      integrations,
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
      release: (typeof import.meta !== 'undefined' && import.meta.env) ? (import.meta.env.VITE_APP_VERSION || '1.0.0') : (process.env.VITE_APP_VERSION || '1.0.0'),
      enabled: ENVIRONMENT === 'production' || ((typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env.VITE_DEBUG_MODE === 'true' : process.env.VITE_DEBUG_MODE === 'true'),
      
      // Error handling configuration
      beforeSend(event, hint) {
        try {
          // Filter out certain errors if needed
          if (hint?.originalException?.message?.includes('Non-Error promise rejection')) {
            return null; // Don't send these errors
          }
          return event;
        } catch (filterError) {
          console.warn('[Monitoring] Error in beforeSend filter:', filterError);
          return event; // Send original event if filter fails
        }
      },
      
      // Performance monitoring configuration
      maxBreadcrumbs: 50,
      debug: (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env.VITE_DEBUG_MODE === 'true' : process.env.VITE_DEBUG_MODE === 'true',
      
      // Transport configuration for better error handling
      transport: Sentry.makeFetchTransport,
      stackParser: Sentry.defaultStackParser,
    });
    
    console.log('[Monitoring] Sentry initialized successfully');
  } catch (error) {
    console.error('[Monitoring] Failed to initialize Sentry:', error);
    console.log('[Monitoring] Falling back to local monitoring');
    initFallbackMonitoring();
  }
}

/**
 * Initialize fallback monitoring when Sentry is not available
 */
function initFallbackMonitoring() {
  console.log('[Monitoring] Initializing fallback monitoring system');
  
  // Store errors locally when Sentry is not available
  window.fallbackMonitoring = {
    errors: [],
    events: [],
    breadcrumbs: [],
    
    captureException: function(error, context = {}) {
      const errorData = {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        context,
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      this.errors.push(errorData);
      console.error('[FallbackMonitoring] Error captured:', errorData);
      
      // Keep only last 100 errors
      if (this.errors.length > 100) {
        this.errors.shift();
      }
      
      // Try to send to our monitoring endpoint
      this.sendToMonitoringService('error', errorData);
    },
    
    captureMessage: function(message, level = 'info', context = {}) {
      const eventData = {
        message,
        level,
        timestamp: new Date().toISOString(),
        context,
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      this.events.push(eventData);
      console.log(`[FallbackMonitoring] ${level} message captured:`, eventData);
      
      // Keep only last 100 events
      if (this.events.length > 100) {
        this.events.shift();
      }
      
      // Try to send to our monitoring endpoint
      this.sendToMonitoringService('event', eventData);
    },
    
    addBreadcrumb: function(category, message, level = 'info', data = {}) {
      const breadcrumb = {
        category,
        message,
        level,
        data,
        timestamp: new Date().toISOString()
      };
      
      this.breadcrumbs.push(breadcrumb);
      console.log(`[FallbackMonitoring] Breadcrumb added:`, breadcrumb);
      
      // Keep only last 50 breadcrumbs
      if (this.breadcrumbs.length > 50) {
        this.breadcrumbs.shift();
      }
    },
    
    sendToMonitoringService: async function(type, data) {
      try {
        const endpoint = type === 'error' ? '/api/monitoring/alerts' : '/api/monitoring/events';
        
        await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': this.getCurrentTenantId()
          },
          body: JSON.stringify({
            ...data,
            type: 'fallback_monitoring',
            originalType: type
          })
        });
        
        console.log(`[FallbackMonitoring] ${type} sent to monitoring service`);
      } catch (error) {
        console.warn(`[FallbackMonitoring] Failed to send ${type} to monitoring service:`, error);
      }
    },
    
    getCurrentTenantId: function() {
      try {
        const tenantData = JSON.parse(localStorage.getItem('tenant_context') || '{}');
        return tenantData.id || 'main';
      } catch (e) {
        return 'main';
      }
    },
    
    getStoredData: function() {
      return {
        errors: this.errors,
        events: this.events,
        breadcrumbs: this.breadcrumbs
      };
    }
  };
}

/**
 * Report an error to Sentry
 */
export function reportError(error, context = {}) {
  if (SENTRY_DSN && typeof Sentry !== 'undefined') {
    try {
      Sentry.withScope((scope) => {
        if (context.userId) {
          scope.setUser({ id: context.userId });
        }
        if (context.tenantId) {
          scope.setTag('tenant_id', context.tenantId);
        }
        if (context.tags) {
          Object.entries(context.tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        }
        if (context.extra) {
          Object.entries(context.extra).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }
        
        Sentry.captureException(error);
      });
    } catch (err) {
      console.error('[Monitoring] Failed to report error to Sentry:', err);
      // Fallback to local monitoring
      if (window.fallbackMonitoring) {
        window.fallbackMonitoring.captureException(error, context);
      }
    }
  } else {
    // Use fallback monitoring
    if (window.fallbackMonitoring) {
      window.fallbackMonitoring.captureException(error, context);
    } else {
      console.warn('[Monitoring] No monitoring system available, error logged to console:', error);
    }
  }
}

/**
 * Capture an event in Sentry
 */
export function captureEvent(message, level = 'info', context = {}) {
  if (SENTRY_DSN && typeof Sentry !== 'undefined') {
    try {
      Sentry.withScope((scope) => {
        if (context.userId) {
          scope.setUser({ id: context.userId });
        }
        if (context.tenantId) {
          scope.setTag('tenant_id', context.tenantId);
        }
        if (context.tags) {
          Object.entries(context.tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        }
        if (context.extra) {
          Object.entries(context.extra).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }
        
        Sentry.captureMessage(message, level);
      });
    } catch (err) {
      console.error('[Monitoring] Failed to capture event in Sentry:', err);
      // Fallback to local monitoring
      if (window.fallbackMonitoring) {
        window.fallbackMonitoring.captureMessage(message, level, context);
      }
    }
  } else {
    // Use fallback monitoring
    if (window.fallbackMonitoring) {
      window.fallbackMonitoring.captureMessage(message, level, context);
    } else {
      console.log(`[Monitoring] No monitoring system available, event logged to console [${level}]:`, message);
    }
  }
}

/**
 * Identify the current user for Sentry
 */
export function identifyUser(userId, user = {}) {
  if (!SENTRY_DSN) {
    return;
  }

  try {
    Sentry.setUser({
      id: userId,
      email: user.email,
      username: user.name || user.email,
      ...user
    });
  } catch (err) {
    console.error('[Monitoring] Failed to identify user in Sentry:', err);
  }
}

/**
 * Clear user identification in Sentry
 */
export function clearUserIdentification() {
  if (!SENTRY_DSN) {
    return;
  }

  try {
    Sentry.setUser(null);
  } catch (err) {
    console.error('[Monitoring] Failed to clear user identification in Sentry:', err);
  }
}

/**
 * Add a breadcrumb to Sentry
 */
export function addBreadcrumb(category, message, level = 'info', data = {}) {
  if (SENTRY_DSN && typeof Sentry !== 'undefined') {
    try {
      Sentry.addBreadcrumb({
        category,
        message,
        level,
        data,
        timestamp: Date.now() / 1000
      });
    } catch (err) {
      console.error('[Monitoring] Failed to add breadcrumb in Sentry:', err);
      // Fallback to local monitoring
      if (window.fallbackMonitoring) {
        window.fallbackMonitoring.addBreadcrumb(category, message, level, data);
      }
    }
  } else {
    // Use fallback monitoring
    if (window.fallbackMonitoring) {
      window.fallbackMonitoring.addBreadcrumb(category, message, level, data);
    }
  }
}

/**
 * Set a tag in Sentry
 */
export function setTag(key, value) {
  if (!SENTRY_DSN) {
    return;
  }

  try {
    Sentry.setTag(key, value);
  } catch (err) {
    console.error('[Monitoring] Failed to set tag in Sentry:', err);
  }
}

/**
 * Set extra data in Sentry
 */
export function setExtra(key, value) {
  if (!SENTRY_DSN) {
    return;
  }

  try {
    Sentry.setExtra(key, value);
  } catch (err) {
    console.error('[Monitoring] Failed to set extra data in Sentry:', err);
  }
}

/**
 * Track page view for analytics
 */
export function trackPageView(pathname, properties = {}) {
  try {
    // Add breadcrumb for page view
    addBreadcrumb('navigation', `Page view: ${pathname}`, 'info', {
      pathname,
      ...properties
    });
    
    // Set page context
    setTag('page', pathname);
    
    console.log(`[Monitoring] Page view tracked: ${pathname}`, properties);
  } catch (error) {
    console.error('[Monitoring] Failed to track page view:', error);
  }
}

/**
 * Get Sentry client for advanced usage
 */
export function getSentryClient() {
  return Sentry;
}

// Export default object with convenience methods
export default {
  initSentry,
  reportError,
  captureEvent,
  identifyUser,
  clearUserIdentification,
  addBreadcrumb,
  setTag,
  setExtra,
  trackPageView,
  getSentryClient
};